// src/components/AttendanceRequestsPane.jsx
// 学生からの申請（出欠の訂正・公欠）を見て、認める・認めない・説明を求める【2026-09-30 / 2026-10-07 部品に切り出し】
//   もとは pages/AttendanceRequests.jsx の中にあった。授業の「出欠の確認・修正」でも使うので部品にした。
//   classId を渡すとその授業の申請だけ（授業の列を出さない）。focus で特定の申請を開いておける。
//   先生は担当の授業、教務は自分の大学の授業の申請が見える（サーバー側で絞っている）。
//   認めると、その回の出席が書き換わる（公欠は出席率の分母から外れる）。
//   【2026-10-21】交通機関の遅れ・運休の申請: 乗った路線と「公式の遅延証明書を確かめる」リンク、学生が付けた画像を出す
import React, { useEffect, useState } from 'react';

// 申請に付いた画像（Bearer が要るので、取ってきて blob の URL で見せる）
function AuthImage({ src, alt }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true, made = null;
    fetch(src, { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } })
      .then(r => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then(b => { if (!alive) return; made = URL.createObjectURL(b); setUrl(made); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [src]);
  if (failed) return <span style={{ fontSize: '0.8rem', color: '#888' }}>（画像を読み込めませんでした）</span>;
  if (!url) return <span style={{ fontSize: '0.8rem', color: '#888' }}>読み込み中…</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img src={url} alt={alt} style={{ maxWidth: 220, maxHeight: 220, borderRadius: 6, border: '1px solid #e5e7eb' }} />
    </a>
  );
}

const FILTERS = [
  { v: 'open',     label: '確認待ち' },
  { v: 'approved', label: '認めた' },
  { v: 'rejected', label: '認めなかった' },
  { v: 'all',      label: 'すべて' },
];
const STATUS_OPTIONS = [
  { v: 'present',     label: '出席' },
  { v: 'late',        label: '遅刻' },
  { v: 'early_leave', label: '早退' },
  { v: 'excused',     label: '公欠' },
];
const BADGE = {
  pending:    { bg: '#fef3c7', fg: '#92400e' },
  needs_info: { bg: '#e0e7ff', fg: '#3730a3' },
  approved:   { bg: '#dcfce7', fg: '#166534' },
  rejected:   { bg: '#fee2e2', fg: '#991b1b' },
  withdrawn:  { bg: '#e5e7eb', fg: '#374151' },
};

export default function AttendanceRequestsPane({ classId = null, focus = null, onChanged = null }) {
  const [filter, setFilter]   = useState('open');
  // 表のマスから「この申請を確認」で来たとき、その申請を開いておく【2026-10-07】
  //   focus = { id, n }（n は押すたびに変わる番号。同じ申請をもう一度押しても開くように）
  const [rows, setRows]       = useState([]);
  const [counts, setCounts]   = useState({ pending: 0, needs_info: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [msg, setMsg]         = useState('');
  const [openId, setOpenId]   = useState(null);
  const [note, setNote]       = useState('');
  const [status, setStatus]   = useState('');
  const [busy, setBusy]       = useState(false);

  const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

  const load = async (f = filter) => {
    setLoading(true); setErr('');
    try {
      const res = await fetch('/api/attendance/requests/inbox?status=' + encodeURIComponent(f)
                              + (classId ? '&classId=' + encodeURIComponent(classId) : ''), { headers: auth() });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || '取得に失敗しました');
      setRows(data.requests || []);
      setCounts(data.counts || { pending: 0, needs_info: 0 });
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(filter); }, [filter, classId]);
  useEffect(() => {
    if (!focus || !focus.id) return;
    const r = rows.find(x => x.id === focus.id);
    if (r) { setOpenId(r.id); setNote(''); setStatus(r.requestedStatus); }
  }, [focus, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (r) => {
    if (openId === r.id) { setOpenId(null); return; }
    setOpenId(r.id); setNote(''); setStatus(r.requestedStatus); setMsg(''); setErr('');
  };

  const decide = async (r, decision) => {
    if (decision === 'needs_info' && !note.trim()) { setErr('学生に求める説明を「コメント」に書いてください'); return; }
    setBusy(true); setErr(''); setMsg('');
    try {
      const body = { decision, note: note.trim() || undefined };
      if (decision === 'approve') body.status = status || r.requestedStatus;
      const res = await fetch(`/api/attendance/requests/${r.id}/decide`, {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || '処理に失敗しました');
      setMsg(`${r.studentName}（${r.studentId}）: ${data.message}`);
      setOpenId(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const day = (ymd) => (ymd ? `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}` : '');

  return (
    <div>
      <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 0 }}>
        学生がアプリから出した「出欠の訂正」「公欠」の申請です。認めるとその回の出席が書き換わります
        （公欠の回は出席率の計算から外れます）。結果は学生のアプリに届きます。
      </p>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '0.5rem 0' }}>
        {FILTERS.map(f => (
          <button key={f.v} className={filter === f.v ? 'btn-primary' : 'btn-secondary'}
                  style={{ padding: '0.25rem 0.8rem' }} onClick={() => setFilter(f.v)}>
            {f.label}{f.v === 'open' && (counts.pending + counts.needs_info) > 0 ? `（${counts.pending + counts.needs_info}）` : ''}
          </button>
        ))}
        <button className="btn-secondary" onClick={() => load()} disabled={loading} style={{ marginLeft: 'auto' }}>
          {loading ? '読み込み中…' : '再読み込み'}
        </button>
      </div>

      {err && <div style={{ color: '#ef4444', margin: '8px 0' }}>{err}</div>}
      {msg && <div style={{ color: '#15803d', margin: '8px 0' }}>{msg}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>授業日</th>{!classId && <th>授業</th>}<th>学生</th><th>申請</th><th>区分</th><th>状態</th><th>届いた日時</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={classId ? 7 : 8} style={{ textAlign: 'center', color: '#888' }}>
                {loading ? '読み込み中…' : '申請はありません'}
              </td></tr>
            )}
            {rows.map(r => (
              <React.Fragment key={r.id}>
                <tr style={focus && focus.id === r.id ? { background: '#eff6ff' } : undefined}>
                  <td style={{ whiteSpace: 'nowrap' }}>{day(r.sessionDate)}{r.sessionNo ? `（第${r.sessionNo}回）` : ''}</td>
                  {!classId && <td>{r.className}</td>}
                  <td>{r.studentName}<div style={{ fontSize: '0.75rem', color: '#888' }}>{r.studentId}</div></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.typeLabel}
                    <div style={{ fontSize: '0.75rem', color: '#555' }}>{r.previousStatusLabel} → {r.requestedStatusLabel}</div>
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>{r.categoryLabel}</td>
                  <td>
                    <span style={{ display: 'inline-block', padding: '0.1rem 0.6rem', borderRadius: 12, fontSize: '0.78rem',
                                   fontWeight: 600, background: (BADGE[r.status] || BADGE.withdrawn).bg,
                                   color: (BADGE[r.status] || BADGE.withdrawn).fg }}>{r.statusLabel}</span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{fmt(r.updatedAt || r.createdAt)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn-secondary" style={{ padding: '0.1rem 0.6rem' }} onClick={() => toggle(r)}>
                      {openId === r.id ? '閉じる' : (r.canEdit ? '確認する' : '詳しく')}
                    </button>
                  </td>
                </tr>
                {openId === r.id && (
                  <tr>
                    <td colSpan={classId ? 7 : 8} style={{ background: '#f9fafb' }}>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>学生が書いた理由</div>
                      <div style={{ whiteSpace: 'pre-wrap', margin: '0.2rem 0 0.6rem', padding: '0.5rem',
                                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6 }}>{r.reason}</div>
                      {r.transit && (
                        <div style={{ margin: '0 0 0.6rem', padding: '0.5rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6 }}>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>乗った交通機関（学生の申告）</div>
                          <div>
                            {r.transit.railwayName}{r.transit.lineName ? `　${r.transit.lineName}` : ''}
                            {'　'}{day(r.transit.date)}{r.transit.departAt ? ` ${r.transit.departAt}ごろ` : ''}
                          </div>
                          {r.transit.delayUrl
                            ? <a href={r.transit.delayUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem' }}>
                                公式の遅延証明書を確かめる（{day(r.transit.date)} のページを見てください）
                              </a>
                            : <div style={{ fontSize: '0.8rem', color: '#888' }}>この会社は Web の遅延証明書を出していません</div>}
                        </div>
                      )}
                      {r.attachments && r.attachments.length > 0 && (
                        <div style={{ margin: '0 0 0.6rem' }}>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>学生が付けた画像（遅延証明書など。真偽は上の公式ページで確かめてください）</div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                            {r.attachments.map(a => <AuthImage key={a.id} src={a.url} alt="添付の画像" />)}
                          </div>
                        </div>
                      )}
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        この回の今の記録: <b>{r.currentStatusLabel}</b>
                        {r.decisionNote && <> ／ 前回のコメント: {r.decisionNote}</>}
                        {r.decidedByName && <> ／ 判断: {r.decidedByName}</>}
                      </div>
                      {r.canEdit && (
                        <div style={{ marginTop: '0.6rem' }}>
                          <label style={{ fontSize: '0.85rem' }}>
                            認めるときの記録:{' '}
                            <select value={status} onChange={e => setStatus(e.target.value)}>
                              {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                            </select>
                          </label>
                          <textarea value={note} onChange={e => setNote(e.target.value)} maxLength={1000} rows={2}
                                    placeholder="学生へのコメント（「説明を求める」ときは必須。病名などは書かないでください）"
                                    style={{ display: 'block', width: '100%', marginTop: 6, boxSizing: 'border-box' }} />
                          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                            <button className="btn-primary" disabled={busy} onClick={() => decide(r, 'approve')}>認める</button>
                            <button className="btn-secondary" disabled={busy} onClick={() => decide(r, 'needs_info')}>説明を求める</button>
                            <button disabled={busy} onClick={() => decide(r, 'reject')}
                                    style={{ color: '#b91c1c' }}>認めない</button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
